import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, ArrowRight, Phone, Loader2, Users, Star, Music, ChevronDown, Plus, CreditCard } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const tierColors = {
  'Essential': 'bg-slate-100 text-slate-800',
  'Intimate': 'bg-emerald-100 text-emerald-800',
  'Premium': 'bg-blue-100 text-blue-800',
  'Professional': 'bg-purple-100 text-purple-800',
  'Elite': 'bg-amber-100 text-amber-800',
  'Flagship': 'bg-rose-100 text-rose-800',
  'Live Band': '',
  'Corporate': ''
};

const tierStyleOverrides = {
  'Live Band': { backgroundColor: '#fde7cb', color: '#b46b15' },
  'Corporate': { backgroundColor: '#d2edf1', color: '#0d7c8c' }
};

const categoryOptions = [
  { value: 'all', label: 'All packages' },
  { value: 'sound-system', label: 'Sound systems' },
  { value: 'band', label: 'Bands' },
  { value: 'dj', label: 'DJ equipment' },
  { value: 'corporate', label: 'Corporate' }
];

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const location = useLocation();

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/packages`);
        if (!response.ok) throw new Error('Failed to fetch packages');
        const data = await response.json();
        setPackages(data);
        setError(null);
      } catch (err) {
        setError('Failed to load packages. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPackages();
  }, []);

  useEffect(() => {
    if (!loading && packages.length > 0 && location.hash) {
      const timer = setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, packages, location.hash]);

  const filteredPackages = selectedCategory === 'all'
    ? packages
    : packages.filter(pkg => pkg.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#e2e8f0] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Error Loading Packages</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button onClick={() => window.location.reload()} className="bg-[#e2e8f0] text-slate-900">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Hero Section */}
      <section className="relative bg-slate-900 py-20 pt-32 border-b border-slate-800">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-[#e2e8f0] text-slate-800 mb-6 px-4 py-1.5 text-xs font-semibold">
            CURATED PACKAGES
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 heading-font">
            Event Rental Packages
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Professional audio rental solutions for private events, luxury parties, and exclusive gatherings.
          </p>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#e2e8f0]">
        <div className="max-w-7xl mx-auto">
          {/* Category Filter */}
          <div className="mb-8 flex items-center gap-3" data-testid="category-filter">
            <span className="text-sm font-medium text-slate-700">Showing</span>
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="appearance-none bg-white border border-slate-300 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400 cursor-pointer"
                data-testid="category-select"
              >
                {categoryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            </div>
          </div>

          <div className="grid gap-8">
            {filteredPackages.map((pkg) => (
              <Card key={pkg.id} id={`package-${pkg.id}`} className="overflow-hidden border-0 shadow-xl bg-white scroll-mt-24" data-testid={`package-card-${pkg.id}`}>
                <div className="flex flex-col lg:flex-row">
                  {/* Image Section */}
                  <div className="relative bg-slate-100 lg:w-2/5">
                    <div className="relative w-full aspect-[16/9] lg:aspect-auto lg:h-full overflow-hidden">
                      {pkg.mainImage ? (
                        <img 
                          src={pkg.mainImage} 
                          alt={pkg.name}
                          className={`w-full h-full object-center hover:scale-105 transition-transform duration-500 ${pkg.zoomOutMain ? 'object-contain bg-slate-900' : 'object-cover'}`}
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-200 flex items-center justify-center min-h-[200px]">
                          <div className="text-center text-slate-400">
                            <Music className="w-12 h-12 mx-auto mb-2 opacity-40" />
                            <p className="text-sm">Image coming soon</p>
                          </div>
                        </div>
                      )}
                      <div className="absolute top-3 left-3">
                        <Badge
                          className={tierColors[pkg.tier] || ''}
                          style={tierStyleOverrides[pkg.tier] || undefined}
                        >
                          {pkg.tier}
                        </Badge>
                      </div>
                      <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-1.5 shadow-lg">
                        <div className="flex items-center gap-2 text-slate-600 text-sm">
                          <Users className="w-4 h-4" />
                          <span className="font-medium">{pkg.capacity}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5 lg:p-6 lg:w-3/5">
                    <div className="flex flex-col h-full">
                      {/* Title row */}
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-slate-800 heading-font">
                            {pkg.name}
                          </h3>
                          <p className="text-gray-600 text-sm mt-1">{pkg.description}</p>
                        </div>
                        <div className="sm:text-right flex-shrink-0">
                          <p className="text-xl font-bold text-slate-800">{pkg.priceRange}</p>
                          <p className="text-xs text-gray-500">excl. VAT</p>
                        </div>
                      </div>

                      {/* Ideal For */}
                      <div className="flex items-start gap-2 mb-3 bg-slate-50 rounded-lg px-3 py-2">
                        <Star className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-gray-600 text-sm"><span className="font-medium text-slate-700">Ideal for:</span> {pkg.idealFor}</p>
                      </div>

                      {/* What's Included */}
                      <div className="mb-3 flex-1">
                        <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2 text-sm">
                          <Music className="w-4 h-4" />
                          What's Included:
                        </h4>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                          {pkg.included.map((item, idx) => (
                            <li key={`${pkg.id}-${item}`} className="flex items-center gap-2 text-sm text-gray-600">
                              <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Note block (Live Band) */}
                      {pkg.note && (
                        <div className="mb-3 bg-slate-50 rounded-lg px-3 py-2">
                          <p className="text-xs text-gray-500 leading-relaxed">{pkg.note}</p>
                        </div>
                      )}

                      {/* Addon line */}
                      {pkg.addon && (
                        <div className="mb-3 flex items-center gap-2">
                          <Plus className="w-3.5 h-3.5 text-slate-800 flex-shrink-0" />
                          <p className="text-xs font-medium text-slate-700">{pkg.addon}</p>
                        </div>
                      )}

                      {/* CTA */}
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between pt-3 border-t border-slate-100 gap-2 sm:gap-3">
                        <p className="text-xs text-gray-500 hidden sm:block">{pkg.footerText || 'Includes delivery, setup, tuning and on-site support'}</p>
                        <div className="flex items-center gap-2 sm:ml-auto w-full sm:w-auto">
                          {pkg.deposit_pence && (
                            <Link to={`/book/${pkg.id}`} className="flex-1 sm:flex-none">
                              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto text-xs sm:text-sm" data-testid={`book-now-btn-${pkg.id}`}>
                                <CreditCard className="w-4 h-4 mr-1.5" />
                                Book
                              </Button>
                            </Link>
                          )}
                          <Link to="/contact" className="flex-1 sm:flex-none">
                            <Button variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100 w-full sm:w-auto text-xs sm:text-sm" data-testid={`request-quote-btn-${pkg.id}`}>
                              Request Quote
                              <ArrowRight className="w-4 h-4 ml-1.5" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Sound System CTA Band */}
          <div className="mt-12 bg-slate-900 rounded-xl px-6 py-10 sm:px-10 sm:py-12 text-center" data-testid="sound-system-cta">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 heading-font">
              Every event needs a sound system.
            </h2>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto mb-6 leading-relaxed">
              Band, DJ and corporate packages cover the equipment and the people. Your Pequod system is quoted separately for your space and occasion. Tell us the room, we will spec the sound.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white hover:bg-gray-100 text-slate-900 shadow-lg font-semibold" data-testid="sound-cta-btn">
                <Phone className="w-4 h-4 mr-2" />
                Get a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Booking Process */}
      <section className="py-16 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-[#e2e8f0] text-slate-800 mb-4 px-4 py-1.5 text-xs font-semibold">HOW IT WORKS</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 heading-font">
              Booking Your Event Package
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                step: '01',
                title: 'Enquiry & Consultation',
                description: 'Contact us with your event details. Date, venue, guest count, and requirements. We will recommend the ideal package.'
              },
              {
                step: '02',
                title: 'Quote & Booking',
                description: 'Receive a detailed quote and secure your booking with a deposit. System availability will be confirmed.'
              },
              {
                step: '03',
                title: 'Setup & Installation',
                description: 'Our experienced team delivers and installs the system, coordinating with your event schedule and venue.'
              },
              {
                step: '04',
                title: 'Tuning & Support',
                description: 'Full system calibration and tuning for your venue, with technical support available throughout the event.'
              }
            ].map((item, idx) => (
              <div key={item.step} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#e2e8f0] flex items-center justify-center shadow-lg">
                  <span className="text-xl font-bold text-slate-900">{item.step}</span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 heading-font">
            Ready to Elevate Your Event?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Contact us today for a personalised quote and let us help you create an unforgettable audio experience.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#e2e8f0] text-slate-900 hover:bg-[#d1d5db] shadow-lg" data-testid="packages-cta-btn">
              <Phone className="w-5 h-5 mr-2" />
              Get Your Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Packages;
